import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import useTranslate from '../../../Hooks/useTranslate';
import { colorByTimeDifference } from '../../../Pages/PrioritiesPage/Components/TimerChip';
import { CenteredFlexBox, ColumnFlexBox } from '../../FlexBox';
import DbeAvatar from '../../DbeAvatar';
import MobileHeader from '../MobileHeader';
import MobilePlaceholder from '../MobilePlaceholder';
import SkeletonList from '../SkeletonList';
import PriorityCard from './PriorityCard';

const URGENT_COLOR = '#cb1d1d';
const LESS_URGENT_COLOR = '#e0a100';

// Same rule as the desktop TimerChip: red (>= 30 min overdue) => urgent.
const isUrgentItem = (item, nowSeconds) => {
  const dateTime = item?.lastRecallDateTime ?? item?.activityDateTime;
  if (!dateTime) return false;
  const diff = nowSeconds - new Date(dateTime).getTime() / 1000;
  return colorByTimeDifference(diff) === URGENT_COLOR;
};

/**
 * MobilePriorities — the Priorità tab. Splits the feed into Urgenti / Meno
 * urgenti using the same timer logic as desktop, and publishes the counter +
 * status avatar to the header. See MOBILE_PLAN.md §F(2).
 */
const MobilePriorities = function MobilePriorities({
  priorities = [],
  loading = false,
}) {
  const theme = useTheme();
  const translate = useTranslate();
  const nowSeconds = Date.now() / 1000;

  const urgent = priorities.filter((item) => isUrgentItem(item, nowSeconds));
  const others = priorities.filter((item) => !isUrgentItem(item, nowSeconds));

  const subtitle = `${priorities.length} ${translate('TO_MANAGE')}${
    urgent.length ? ` · ${urgent.length} ${translate('URGENT')}` : ''
  }`;

  // Header status glyph (like the desktop PrioritiesAlert).
  let statusIcon = <DoneRoundedIcon />;
  let statusColor = theme.palette.success.main;
  if (urgent.length) {
    statusIcon = <PriorityHighRoundedIcon />;
    statusColor = theme.palette.error.main;
  } else if (priorities.length) {
    statusIcon = <WarningAmberRoundedIcon />;
    statusColor = theme.palette.warning.main;
  }

  const renderSection = (labelKey, color, items) =>
    items.length ? (
      <ColumnFlexBox sx={{ gap: 1.25 }}>
        <CenteredFlexBox sx={{ gap: 0.75 }}>
          <Box
            sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color }}
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              letterSpacing: '.06em',
              textTransform: 'uppercase',
              color,
            }}
          >
            {translate(labelKey)}
          </Typography>
        </CenteredFlexBox>
        {items.map((item) => (
          <PriorityCard key={item?.id} data={item} accentColor={color} />
        ))}
      </ColumnFlexBox>
    ) : null;

  const body = loading ? (
    <SkeletonList count={6} height={78} />
  ) : priorities.length ? (
    <ColumnFlexBox sx={{ p: 1.5, gap: 1 }}>
      {renderSection('URGENT', URGENT_COLOR, urgent)}
      {renderSection('LESS_URGENT', LESS_URGENT_COLOR, others)}
    </ColumnFlexBox>
  ) : (
    <MobilePlaceholder
      icon={<BoltRoundedIcon sx={{ fontSize: 40 }} />}
      title={translate('PRIORITIES')}
      subtitleKey="MOBILE_NO_ITEMS"
    />
  );

  return (
    <>
      <MobileHeader
        subtitle={subtitle}
        avatar={<DbeAvatar icon={statusIcon} color={statusColor} />}
      />
      {body}
    </>
  );
};

export default MobilePriorities;
